"use client";

import React from "react";
import { ExternalLink, BookOpen } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AspectRatio } from "@/components/ui/aspect-ratio";

function SingleBookConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto flex max-w-4xl flex-col items-center px-4 py-16 text-center animate-pulse">
        <div className="mb-8 w-full max-w-md">
          <AspectRatio ratio={2 / 3}>
            <div className="h-full w-full rounded-lg bg-muted shadow-xl" />
          </AspectRatio>
        </div>
        <div className="mb-4 h-6 w-24 rounded-md bg-muted" />
        <div className="mb-6 h-12 w-3/4 rounded-md bg-muted" />
        <div className="mb-2 h-4 w-full max-w-2xl rounded-md bg-muted" />
        <div className="mb-2 h-4 w-full max-w-2xl rounded-md bg-muted" />
        <div className="mb-10 h-4 w-2/3 max-w-2xl rounded-md bg-muted" />
        <div className="flex w-full flex-col justify-center gap-4 sm:w-auto sm:flex-row">
          <div className="h-12 w-full rounded-md bg-muted sm:w-48" />
          <div className="h-12 w-full rounded-md bg-muted sm:w-48" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto flex max-w-4xl flex-col items-center px-4 py-16 text-center">
        <p className="text-muted-foreground">Unable to load book details at this time.</p>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="mx-auto flex max-w-4xl flex-col items-center px-4 py-16 text-center">
        <BookOpen className="mb-4 h-12 w-12 text-muted-foreground" />
        <h2 className="font-serif text-2xl font-semibold text-foreground">Book Not Found</h2>
        <p className="mt-2 text-muted-foreground">The book you are looking for does not exist or has been removed.</p>
        <Link to="/BooksPage" className="mt-6">
          <Button variant="outline">Return to Books</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];
  const customFields = post.customFields || {};
  
  const displayTitle = customFields.title || post.title;
  const displayDescription = customFields.description || post.content || post.excerpt;
  const displayImage = customFields.book_cover_image || post.featuredImage;
  const topic = customFields.topic;
  const amazonLink = customFields.amazon_link;

  return (
    <article className="mx-auto flex max-w-4xl flex-col items-center px-4 py-16 text-center md:py-24">
      {displayImage && (
        <div className="mb-10 w-full max-w-md">
          <AspectRatio ratio={2 / 3}>
            <img
              src={displayImage}
              alt={`Cover of ${displayTitle}`}
              className="h-full w-full rounded-lg object-cover shadow-2xl ring-1 ring-border/50"
              loading="eager"
            />
          </AspectRatio>
        </div>
      )}

      {topic && (
        <Badge 
          variant="secondary" 
          className="mb-6 bg-accent/10 px-4 py-1.5 text-sm font-medium text-accent-foreground hover:bg-accent/20"
        >
          {topic}
        </Badge>
      )}

      <h1 className="mb-6 font-serif text-4xl font-bold leading-tight text-foreground md:text-5xl lg:text-6xl">
        {displayTitle}
      </h1>

      {displayDescription && (
        <div className="prose prose-lg mb-12 max-w-2xl whitespace-pre-wrap text-muted-foreground">
          {displayDescription}
        </div>
      )}

      <div className="flex w-full flex-col justify-center gap-4 sm:w-auto sm:flex-row">
        {amazonLink && (
          <Link to={amazonLink} newTab>
            <Button size="lg" className="w-full sm:w-auto">
              Buy on Amazon
              <ExternalLink className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        )}
        <Link to="/BooksPage">
          <Button size="lg" variant="outline" className="w-full sm:w-auto">
            View More Books
          </Button>
        </Link>
      </div>
    </article>
  );
}

export default function SingleBookTemplateSection() {
  
  const currentWPQueryParams = typeof window !== "undefined" && (window as any).wvcClient 
    ? (window as any).wvcClient.getCurrentWPQueryParams() 
    : { post_type: "book" };
    
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section id="single-book-template-section" className="min-h-screen bg-background">
      <PostsProvider wp_query={wp_query}>
        <SingleBookConsumer />
      </PostsProvider>
    </section>
  );
}